/**
 * Download a photo or video to the user's device.
 * When downloadApiPath is provided (same-origin API), fetches that URL and triggers a direct download.
 * Otherwise fetches the given url (e.g. S3 signed URL) as blob and triggers download, with fallback to open in new tab.
 */
export async function downloadPhotoOrVideo(
  url: string,
  options: {
    id: number | string
    mediaType?: 'image' | 'video' | 'document'
    /** Same-origin API path that returns the file with Content-Disposition: attachment (e.g. /api/base-photos/123/download). When set, used instead of url for the request so the browser downloads directly. */
    downloadApiPath?: string
  }
): Promise<void> {
  const { id, mediaType = 'image', downloadApiPath } = options
  const ext =
    mediaType === 'video' ? 'mp4' : mediaType === 'document' ? 'pdf' : 'jpg'
  const fallbackFilename = `reliefhub-${mediaType}-${id}.${ext}`

  const doDownload = async (fetchUrl: string, filename: string) => {
    const res = await fetch(fetchUrl, { mode: 'cors', credentials: 'include' })
    if (!res.ok) throw new Error(`Download failed: ${res.status}`)
    const blob = await res.blob()
    const disposition = res.headers.get('Content-Disposition')
    const suggestedName = disposition?.match(/filename="?([^";\n]+)"?/)?.[1]?.trim()
    const finalFilename = suggestedName || filename
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = finalFilename
    a.click()
    URL.revokeObjectURL(blobUrl)
  }

  try {
    if (downloadApiPath) {
      await doDownload(downloadApiPath, fallbackFilename)
      return
    }
    await doDownload(url, fallbackFilename)
  } catch (e) {
    console.error('Download error:', e)
    if (!downloadApiPath) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }
}
